/**
 * Defaults for synced settings (`settings` rows) and local settings (`local_settings` rows).
 * A missing row reads as its default; nothing is seeded, so two fresh devices start identical.
 */

import type { FolderId } from '../contracts/ids.js';
import type { LocalSettings, SyncedSettings, ThemeDefinition } from '../contracts/settings.js';

/** The prototype's custom theme defaults (design/reboot/themes.js `defaults()`). */
export const DEFAULT_CUSTOM_THEME: ThemeDefinition = {
  name: 'My theme',
  base: 'dark',
  uiFont: 'Inter',
  readingFont: 'Inter',
  uiFamily: '',
  readingFamily: '',
  shadows: true,
  colors: {
    page: '#121216',
    nav: '#18181e',
    list: '#1b1b22',
    reader: '#202027',
    details: '#19191f',
    surface: '#272730',
    'surface-hover': '#30303b',
    border: '#303039',
    'border-light': '#41414d',
    text: '#eae8ef',
    body: '#d7d4df',
    muted: '#bbb5c6',
    dim: '#aaa3b4',
    accent: '#cbb7ff',
    'accent-fill': '#70579f',
    'accent-bg': '#383047',
    selected: '#2e293b',
    green: '#a5ceb3',
  },
  uiSize: 15,
  readingSize: 18,
  headingSize: 28,
  uiLineHeight: 1.5,
  readingLineHeight: 1.8,
  letterSpacing: 0,
  rowPadding: 16,
  readerPadding: 34,
  sectionGap: 26,
  controlHeight: 42,
  toolbarHeight: 72,
  radius: 8,
  borderWidth: 1,
  iconSize: 20,
  navWidth: 190,
  listWidth: 360,
  detailsWidth: 292,
  articleWidth: 700,
};

export const SYNCED_SETTINGS_DEFAULTS: SyncedSettings = {
  profile: { name: '', timezone: 'UTC', language: 'en', notifications: false },
  appearance: { theme: 'dark', accent: 'violet', density: 'comfortable' },
  reading: { fontSize: 18, showImages: true, readOnOpen: true },
  // bodyChars 1,000: the title and opening carry the topic (Jev's 0.95 AUC was measured on messages of ~900
  // characters); with the 13 topic questions (~600 tokens) it roughly halves the ~1,600 tokens a message
  // that the full 6,000 characters cost (open-decisions.md 40).
  scoring: { enabled: true, scoreFiltered: true, dailyLimit: null, bodyChars: 1000 },
  customTheme: DEFAULT_CUSTOM_THEME,
};

/** `deviceId` has no default: the storage layer generates it on first open (newDeviceId). */
export const LOCAL_SETTINGS_DEFAULTS: Omit<LocalSettings, 'deviceId'> = {
  ui: { detailsOpen: true, sidebarCollapsed: false, lastView: { kind: 'folder', id: 'inbox' as FolderId } },
  entitlement: null,
  entitlementFetchedAt: null,
  account: null,
  installToken: null,
  onboarded: false,
};
